import { supabase } from '../components/data/supabaseClient';
import { polylineToWktLineString, processPolyline } from './polylineUtils';

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

/**
 * Improved implementation of route point generation that handles both formats
 * This uses direct SQL inserts instead of the generate_route_points function
 */
export async function manualGenerateRoutePoints(polyline, { postId = null, packageId = null, groupRideId = null, intervalKm = 5 }) {
  try {
    console.log('Starting manual route point generation with:', {
      postId: postId ? `ID: ${postId}` : 'null', 
      packageId: packageId ? `ID: ${packageId}` : 'null',
      groupRideId: groupRideId ? `ID: ${groupRideId}` : 'null',
      polylineLength: polyline ? polyline.length : 0
    });
    
    // Ensure groupRideId is a valid UUID string if provided
    if (groupRideId && typeof groupRideId === 'string') {
      // Validate UUID format
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(groupRideId)) {
        console.error('Invalid UUID format for groupRideId:', groupRideId);
        return { success: false, error: 'Invalid UUID format for groupRideId' };
      }
    }

    if (!polyline) {
      console.error('No polyline provided');
      return { success: false, error: 'No polyline provided' };
    }

    // Use the improved function that automatically detects format
    const points = processPolyline(polyline);
    
    if (points.length < 2) {
      console.error('Not enough points extracted from polyline, got:', points.length);
      return { success: false, error: 'Not enough points in polyline' };
    }
    
    console.log(`Processed ${points.length} points from polyline`);
    
    // Convert points from [lat, lng] to [lng, lat] for PostGIS if needed
    // Points from processPolyline come as [lat, lng]
    // Points need to be in [lng, lat] format for PostGIS
    const postgisPoints = points.map(point => [point[1], point[0]]);
    
    // First, delete existing points
    if (postId) {
      await supabase
        .from('route_points')
        .delete()
        .eq('post_id', postId);
      
      console.log(`Deleted existing route points for post ${postId}`);
    } else if (packageId) {
      await supabase
        .from('route_points')
        .delete()
        .eq('package_id', packageId);
        
      console.log(`Deleted existing route points for package ${packageId}`);
    } else if (groupRideId) {
      await supabase
        .from('route_points')
        .delete()
        .eq('group_ride_id', groupRideId);
        
      console.log(`Deleted existing route points for group ride ${groupRideId}`);
    }
    
    // Insert start point
    const startPoint = postgisPoints[0];
    const { error: startError } = await supabase
      .from('route_points')
      .insert({
        post_id: postId,
        package_id: packageId,
        group_ride_id: groupRideId,
        point_order: 1,
        location: `POINT(${startPoint[0]} ${startPoint[1]})`,
        is_start: true,
        is_end: false,
        created_at: new Date().toISOString()
      });
      
    if (startError) {
      console.error('Error inserting start point:', startError);
      return { success: false, error: 'Error inserting start point: ' + startError.message };
    }
    
    // Calculate total route distance to determine sampling interval
    let totalDistance = 0;
    for (let i = 1; i < points.length; i++) {
      totalDistance += calculateDistance(
        points[i-1][0], points[i-1][1],
        points[i][0], points[i][1]
      );
    }
    
    // Convert m to km
    totalDistance = totalDistance / 1000;
    console.log(`Total route distance calculated: ${totalDistance.toFixed(2)} km`);
    
    // Calculate how many points to sample based on route length
    // Use at least 3 points for short routes, or one point per intervalKm
    const optimalPointCount = Math.max(3, Math.ceil(totalDistance / intervalKm));
    const samplingInterval = Math.max(1, Math.floor(postgisPoints.length / optimalPointCount));
    
    console.log(`Using sampling interval of ${samplingInterval} points to create ~${optimalPointCount} route points`);
    
    // Insert sample points using the calculated interval
    let pointsInserted = 1; // Start point already inserted
    
    for (let i = samplingInterval; i < postgisPoints.length - samplingInterval; i += samplingInterval) {
      const { error: sampleError } = await supabase
        .from('route_points')
        .insert({
          post_id: postId,
          package_id: packageId,
          group_ride_id: groupRideId,
          point_order: pointsInserted + 1,
          location: `POINT(${postgisPoints[i][0]} ${postgisPoints[i][1]})`,
          is_start: false,
          is_end: false,
          created_at: new Date().toISOString()
        });
        
      if (sampleError) {
        console.error(`Error inserting sample point ${i}:`, sampleError);
        // Continue despite errors
      } else {
        pointsInserted++;
      }
    }
    
    // Insert end point
    const endPoint = postgisPoints[postgisPoints.length - 1];
    const { error: endError } = await supabase
      .from('route_points')
      .insert({
        post_id: postId,
        package_id: packageId,
        group_ride_id: groupRideId,
        point_order: pointsInserted + 1,
        location: `POINT(${endPoint[0]} ${endPoint[1]})`,
        is_start: false,
        is_end: true,
        created_at: new Date().toISOString()
      });
      
    if (endError) {
      console.error('Error inserting end point:', endError);
      // Continue despite errors
    } else {
      pointsInserted++;
    }
    
    console.log(`Manual route points generation complete. Created ${pointsInserted} points.`);
    return { success: true, pointsCreated: pointsInserted };
  } catch (error) {
    console.error('Exception in manualGenerateRoutePoints:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Generate route points from a Google Maps encoded polyline
 * Stores the points in the route_points table for route matching
 * 
 * @param {string} polyline - Google Maps encoded polyline or custom format
 * @param {object} options - Options for route point generation
 * @param {number|null} options.postId - Post ID if generating for a post
 * @param {string|null} options.packageId - Package ID if generating for a package
 * @param {number} options.intervalKm - Interval in km between sampled points
 * @returns {Promise<object>} Result of the operation
 */
export async function generateRoutePoints(polyline, { postId = null, packageId = null, groupRideId = null, intervalKm = 5 }) {
  try {
    console.log('Starting route point generation with:', {
      postId: postId ? `ID: ${postId}` : 'null', 
      packageId: packageId ? `ID: ${packageId}` : 'null',
      groupRideId: groupRideId ? `ID: ${groupRideId}` : 'null',
      polylineLength: polyline ? polyline.length : 0,
      polylineSample: polyline ? polyline.substring(0, 30) + '...' : 'null'
    });

    if (!polyline) {
      console.error('No polyline provided');
      return { success: false, error: 'No polyline provided' };
    }

    console.log('Polyline first 30 chars:', polyline.substring(0, 30) + '...');

    // Use the improved manual implementation that supports both formats
    return manualGenerateRoutePoints(polyline, { postId, packageId, groupRideId, intervalKm });
  } catch (error) {
    console.error('Exception in generateRoutePoints:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Find matches for a route within a specified radius
 * 
 * @param {object} options - Search options
 * @param {number|null} options.postId - Post ID if searching from a post
 * @param {string|null} options.packageId - Package ID if searching from a package
 * @param {string|null} options.groupRideId - Group Ride ID if searching from a group ride
 * @param {boolean} options.isPackage - Whether searching for package matches
 * @param {boolean} options.isGroupRide - Whether searching for group ride matches
 * @param {string|null} options.role - Filter by role (Driver, Passenger, or NULL for all)
 * @param {number} options.radiusKm - Search radius in kilometers
 * @returns {Promise<Array>} Array of matching routes
 */
export async function findRouteMatches({ 
  postId = null, 
  packageId = null,
  groupRideId = null,
  isPackage = false,
  isGroupRide = false, 
  role = null, 
  radiusKm = 10 
}) {
  try {
    console.log('Searching for route matches with params:', { 
      postId, 
      packageId, 
      groupRideId, 
      isPackage, 
      isGroupRide, 
      role, 
      radiusKm 
    });

    // Convert parameters to proper types to match SQL function signature
    const params = {
      p_route_id: postId ? parseInt(postId, 10) : null,
      p_is_package: isPackage,
      p_package_id: packageId,
      p_group_ride_id: groupRideId,
      p_is_group_ride: isGroupRide,
      p_radius_km: parseFloat(radiusKm),
      p_role: role
    };

    console.log('Calling find_route_matches with params:', params);

    const { data, error } = await supabase.rpc('find_route_matches', params);
    
    if (error) {
      console.error('Error finding route matches:', error);
      throw error;
    }
    
    // Process the data to ensure correct types
    const processedData = data?.map(match => ({
      ...match,
      // Ensure numeric fields are properly typed
      distance_km: parseFloat(match.distance_km),
      start_point_lat: parseFloat(match.start_point_lat),
      start_point_lng: parseFloat(match.start_point_lng),
      end_point_lat: parseFloat(match.end_point_lat),
      end_point_lng: parseFloat(match.end_point_lng)
    })) || [];

    console.log('Found matches:', processedData.length);
    return processedData;
  } catch (error) {
    console.error('Exception in findRouteMatches:', error);
    throw error;
  }
}